import React, { SVGProps, useEffect, useState } from "react";

interface SvgIconProps extends SVGProps<SVGSVGElement> {
    name: string;
    color?: string;
    size?: number;
}

type IconComponent = React.FC<SVGProps<SVGSVGElement>>;

// Icons load once per name for the app's lifetime. A list mounts hundreds of
// icons at a time; without the cache each one ran its own import and set
// state around it, and those updates added up to a visible stall.
const loaded = new Map<string, IconComponent>();
const loading = new Map<string, Promise<IconComponent | undefined>>();

function loadIcon(name: string): Promise<IconComponent | undefined> {
    let request = loading.get(name);
    if (!request) {
        request = import(`!!@svgr/webpack?svgo=false!../../assets/icons/svg/${name}.svg`)
            .then((res) => {
                loaded.set(name, res.default);
                return res.default as IconComponent;
            })
            .catch((err) => {
                console.error(err);
                loading.delete(name); // retry on the next mount
                return undefined;
            });
        loading.set(name, request);
    }
    return request;
}

export const SvgIcon: React.FC<SvgIconProps> = ({
    name,
    size = 16,
    color = "currentColor",
    ...restProps
}) => {
    // Synchronous when the icon is already cached: no effect, no state update.
    const [icon, setIcon] = useState<IconComponent | undefined>(() => loaded.get(name));

    useEffect(() => {
        const cached = loaded.get(name);
        if (cached) {
            if (cached !== icon) setIcon(() => cached);
            return;
        }
        let cancelled = false;
        void loadIcon(name).then((component) => {
            if (!cancelled) setIcon(() => component);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    if (!icon) return null;
    const ImportedIcon = icon;
    return (
        <span style={{ display: "inline-block", verticalAlign: "-0.125em", lineHeight: 0 }}>
            <ImportedIcon
                width={size}
                height={size}
                style={{ width: size, height: size }}
                fill={color}
                {...restProps}
            />
        </span>
    );
};
